
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// import { transparentStyles } from "components/LandingPage/ClearwaysSidebar/styles";

import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

import { Grid, Tabs, Tab, Box, Card, Divider, Icon, Switch } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

import { useSoftUIController, setTransparentInfo, setMiniSidenav, setHiddenSidenav, setDeviceLayout } from "hooks/context";

import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

import WeatherProvider from "components/Weather";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import theme from "assets/theme";
import moment from "moment";
import SoftButton from "components/SoftComponents/SoftButton";
import StatsPieChart from "components/Charts/StatisticCards/StatsPieChart";
import UptimeDoughnutChart from "components/Charts/StatisticCards/UptimeDoughnutChart";
import { useLocation, useNavigate } from "react-router-dom";
import SoftBadgeDot from "components/SoftComponents/SoftBadgeDot";
import { customSidebarStyle, sidebarHeadersStyle, sidebarTabStyle, sidebarToggleStyle, exploreContentStyle } from "../styles/index";
import SidebarRoutes from "../NavRoutes";

import groupAPI from "services/api/groups";
import deviceAPI from "services/api/bks";
import userAPI from 'services/api/user';
import GroupLiveView from "components/Groups/GroupLiveView";
import { processDevicesData } from "utils/device";
import { processGroupData } from "utils/group";
import EventsBar from "modules/clearways/views/EventsBar";
import { setMiniNavbar } from "hooks/context";
import { sidebarWrapper } from "utils/wrapper";

const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;
const { dark, white, text, transparent } = palette;
const { navbarBoxShadow } = boxShadows;
const { rgba, pxToRem } = functions;
const { borderRadius } = borders;

function getGroupData(devices_data, group_data, show_inactive = false) {
    var data = {};
    var showing_all = true;

    if (group_data != null) {
        if (group_data.length != 0) {
            showing_all = false;
        }
    }

    if (showing_all) {
        var data = devices_data;
        data.name = "All Devices";
        data.devices = processDevicesData(devices_data, show_inactive);//, group_data.type);
    }
    else {
        data = processGroupData(group_data, devices_data, show_inactive);
    }

    data.showing_all = showing_all;

    return data;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, noHeight = false, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: `calc(100% - 6.5rem - 16px)` }}
        >
            {(value === index && noHeight) && <Box>{children}</Box>}
            {(value === index && !noHeight) && <Box sx={{ height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function ClearwaysSidebar({ user, groupPreviewID = 0, events, showDevices, setShowDevices }) {
    const [controller, dispatch] = useSoftUIController();
    const { transparentInfo, deviceLayout, miniNavbar } = controller;
    const navigate = useNavigate();

    const toggleMiniNavbar = () => setMiniNavbar(dispatch, !miniNavbar);

    const [tabValue, setTabValue] = useState(0);

    const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    deviceAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await deviceAPI.getAllDevices();
        }
    });

    const groups = useQuery({
        queryKey: ['getAllGroups'],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', groupPreviewID],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(groupPreviewID);
        }
    });

    if (groups.isLoading || devices.isLoading) {
        return ('Loading...');
    }

    if (groups.error || devices.error) {
        return ("An error has occurred");
    }

    if (!devices.data) {
        return ('Loading...');
    }

    var group_data = getGroupData(devices.data, group.data);
    var position = { latitude: 0, longitude: 0 };

    try {
        var lat = devices.data.map((element) => element.position.latitude);
        position.latitude = lat.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        var lng = devices.data.map((element) => element.position.longitude);
        position.longitude = lng.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        if (isNaN(position.latitude) || isNaN(position.longitude)) {
            var position = { latitude: 33.8688, longitude: 151.2093 };
        }
    }
    catch (e) {
        console.log('improper device data provided');
    }

    const colours = ['success', 'warning', 'error'];
    const renderBadgeDots = ['Full', 'Healthy', 'Low'].map((label, index) => {
        return (
            <SoftBadgeDot
                key={`badge-dot-${index}`}
                variant="gradient"
                color={colours[index]}
                size="lg"
                badgeContent={label}
                font={{ color: "text", weight: "medium" }}
                px={0}
            />
        );
    });

    var liveview_cards = groups.data.filter(row => (groupPreviewID == 0 || row.parent_id == groupPreviewID || row.group_id == groupPreviewID) && row.type == "clearways").map(function (row) {
        var ret = processGroupData(row, devices.data);
        return ret;
    }).sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    }).map(function (row) {
        if (row.devices.length > 0) {
            return (
                <Grid item key={row.group_id} xxs={12} md={6} xl={12}>
                    <Card sx={{ paddingBottom: '12px', height: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                        <Grid container>
                            <Grid item xxs={12}>
                                <SoftBox sx={{ height: '100%', width: '100%' }} >
                                    <SoftBox sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                                        <GroupLiveView group={row} />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xxs={12}>
                                <SoftBox pb={1.5} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', textAlign: 'center' }}>
                                    <SoftTypography variant='h5' textTransform='capitalize' fontWeight='bold'>
                                        {row.name}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            );
        }
    }).filter(row => typeof row != 'undefined');

    return (
        <SoftBox sx={{ height: '100%', paddingBottom: '8px' }}>
            {sidebarWrapper((
                <SoftBox height={miniNavbar ? "70px" : "100%"} sx={{ overflow: 'hidden' }}>
                    <SoftBox p={1}>
                        <Grid container spacing={1}>
                            <Grid item xxs={showDevices == null ? 12 : 6}>
                                <SoftButton fullWidth onClick={toggleMiniNavbar} sx={{ height: '100%', padding: 0, lineHeight: 4.2 }}>Toggle Menu</SoftButton>
                            </Grid>
                            {showDevices != null && (
                                <Grid item xxs={6}>
                                    <SoftBox display="flex" flexDirection="row" justifyContent="center" alignItems="center" ml={0.5} px={0.5} py={1}
                                        sx={({ palette: { white }, functions: { rgb } }) => ({
                                            borderRadius: '8px', backgroundColor: rgb(white.main),
                                            boxShadow: "0rem 0.625rem 0.75rem 0rem rgba(0, 0, 0, 0.05)",
                                            minHeight: '28px'
                                        })}>
                                        <Grid container alignItems="center">
                                            <Grid item xxs={6}>
                                                <SoftBox display="flex" justifyContent="center" sx={{ paddingTop: '2px' }}>
                                                    <Switch checked={showDevices} onChange={() => setShowDevices(!showDevices)} />
                                                </SoftBox>
                                            </Grid>
                                            <Grid item xxs={6}>
                                                <SoftBox pr={1} display="flex" justifyContent="center" sx={{ textAlign: "center" }}>
                                                    <SoftTypography variant="caption">Show Devices</SoftTypography>
                                                </SoftBox>
                                            </Grid>
                                        </Grid>
                                    </SoftBox>
                                </Grid>
                            )}
                        </Grid>
                    </SoftBox>
                    <Grid container display={miniNavbar ? 'none' : 'flex'}>
                        <Grid item xxs={12} sm={12} mx={1} mb={1} sx={{ width: 'calc(100% - 16px)' }}>
                            <Tabs value={tabValue}
                                onChange={handleTabClick} aria-label="ClearwaysSidebar Tabs"
                                allowScrollButtonsMobile
                                variant="scrollable"
                                scrollButtons
                                sx={{
                                    display: miniNavbar ? 'none' : 'flex',
                                    opacity: 0.8,
                                    [`& .${tabsClasses.scrollButtons}`]: {
                                        '&.Mui-disabled': { opacity: 0.3 },
                                    },
                                }}
                            >
                                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Liveviews" {...a11yProps(0)} />
                                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Statistics" {...a11yProps(1)} />
                                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Upcoming Events" {...a11yProps(2)} />
                            </Tabs>
                        </Grid>
                    </Grid>
                    <CustomTabPanel value={tabValue} index={0}>
                        <SoftBox pb={1}>
                            <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                                <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { miniNavbar })}>
                                    <SoftBox px={1} height={deviceLayout == "mobile" ? "calc(100svh - 11.85rem - 16px)" : "calc(100svh - 13.75rem - 16px)"} sx={{ overflowY: 'auto' }}>
                                        {liveview_cards.length > 0 && (
                                            <SoftBox sx={{ height: '100%' }}>
                                                <Grid container spacing={1}>
                                                    {liveview_cards}
                                                </Grid>
                                            </SoftBox>
                                        )}
                                        {liveview_cards.length == 0 && (
                                            <SoftBox display='flex' flexDirection='column' justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
                                                <SoftTypography variant={window.innerWidth < breakpoints.up("sm") ? "h6" : "h5"}>
                                                    No Previews Available!
                                                </SoftTypography>
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </SoftBox>
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        <SoftBox height={deviceLayout == "mobile" ? "calc(100svh - 11.85rem - 16px)" : "calc(100svh - 13.75rem - 16px)"}>
                            <Grid container>
                                <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { miniNavbar })}>
                                    <SoftBox px={1} height={deviceLayout == "mobile" ? "calc(100svh - 11.85rem - 16px)" : "calc(100svh - 13.75rem - 16px)"} sx={{ overflowY: 'auto' }}>
                                        <Grid container spacing={1}>
                                            <Grid item xxs={12}>
                                                <UptimeDoughnutChart
                                                    icon="percent"
                                                    color="secondary"
                                                    title="All Devices"
                                                    devices={group_data.showing_all ? group_data : group_data.devices}
                                                    process={false}
                                                    user={user}
                                                    height={deviceLayout == 'mobile' ? '100px' : 'unset'}
                                                    maxHeight='180px'
                                                />
                                            </Grid>
                                            <Grid item xxs={12}>
                                                <Card>
                                                    <SoftBox display="flex" flexDirection="row" justifyContent={'space-evenly'}>
                                                        {renderBadgeDots}
                                                    </SoftBox>
                                                </Card>
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12}>
                                                <StatsPieChart
                                                    title="Battery Levels"
                                                    devices={group_data.devices}
                                                    data_type={"battery"}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12}>
                                                <StatsPieChart
                                                    title="Signal Levels"
                                                    devices={group_data.devices}
                                                    data_type={"rssi"}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12}>
                                                <StatsPieChart
                                                    title="Temperature Levels"
                                                    devices={group_data.devices}
                                                    data_type={"temperature"}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12}>
                                                <StatsPieChart
                                                    title="Humidity Levels"
                                                    devices={group_data.devices}
                                                    data_type={"humidity"}
                                                />
                                            </Grid>
                                        </Grid>
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </SoftBox>
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={2}>
                        <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                            <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { miniNavbar })}>
                                <EventsBar events_data={events} />
                            </Grid>
                        </Grid>
                    </CustomTabPanel>
                </SoftBox>
            ), controller)}
        </SoftBox>
    );
}

ClearwaysSidebar.propTypes = {
    user: PropTypes.object,
    groupPreviewID: PropTypes.number || PropTypes.string,
    events: PropTypes.array,
    showDevices: PropTypes.bool,
    setShowDevices: PropTypes.func
}

export default ClearwaysSidebar;
